import { Box } from '@atgfr8/atg-ui-tailwind';
import {
  initialFormValues,
  stopValidations,
  type InitialStopFormValues,
} from '@common/stops';
import { appointmentSchedulingStopFields } from '@common/stops/appointmentScheduling';
import {
  copyFacilityToStop,
  createStopFacilityDetails,
  normalizeFacilityDetails,
} from '@common/facilityDetails';
import { LTLProductCatalog, MenuItemType, ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { createForm } from '@felte/solid';
import { validateSchema } from '@felte/validator-yup';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { ltlQuoteState, setLtlQuoteState } from '@store/ltl/store';
import { closeModal, openModal } from '@store/modals';
import {
  CustomerAddress,
  EdiUpdate,
  LTLCatalogs,
  PickUpDropOffItem,
  featureFlagGetCustomerFeatures,
  orderStore,
  setOrderStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { LoadModeTypes } from '@typeDefinitions/loadTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import {
  fillBreakdownErrorHoles,
  mapLoadBreakdownsToQuoteBreakdowns,
} from '@utils/ltl/loadItemBreakdown';
import { timeIntegerToTimeString } from '@utils/dateFormat';
import { convertDateTimeV2, handleToast } from '@utils/utils';
import AddressBookGrid from '@views/customer/customerDetail/addressBookTab/AddressBookGrid';
import find from 'lodash/find';
import get from 'lodash/get';
import { DateTime } from 'luxon';
import {
  Accessor,
  Setter,
  Show,
  createEffect,
  createMemo,
  createSignal,
} from 'solid-js';

const copyFacilityDetailsToStop = (
  rowData: CustomerAddress,
  setFields: (key: string, value: unknown) => void,
  mode: LoadModeTypes,
) => {
  if (
    mode !== 'TL' ||
    !isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)()
  ) {
    return;
  }

  // Copy the facility's values onto the stop and link the two. The stop keeps its own
  // explicit values, so deleting the facility later leaves this stop intact.
  const facilityDetails = rowData.facilityDetails
    ? copyFacilityToStop(rowData.facilityDetails, rowData.id ?? null)
    : {
        ...createStopFacilityDetails(),
        customerAddressId: rowData.id ?? null,
        appointmentType: rowData.appointmentType ?? '',
        schedulingMethod: rowData.schedulingMethod ?? '',
      };
  setFields('pickupHoursFrom', rowData.pickupHoursFrom ?? '');
  setFields('pickupHoursTo', rowData.pickupHoursTo ?? '');
  setFields('pickupHoursClosed', Boolean(rowData.pickupHoursClosed));
  setFields('pickupHoursNotes', rowData.pickupHoursNotes ?? '');
  setFields('receivingHoursFrom', rowData.receivingHoursFrom ?? '');
  setFields('receivingHoursTo', rowData.receivingHoursTo ?? '');
  setFields('receivingHoursClosed', Boolean(rowData.receivingHoursClosed));
  setFields(
    'receivingUsePickupHours',
    Boolean(rowData.receivingUsePickupHours),
  );
  setFields('receivingHoursNotes', rowData.receivingHoursNotes ?? '');
  setFields('facilityAppointmentType', rowData.appointmentType ?? '');
  setFields('facilitySchedulingMethod', rowData.schedulingMethod ?? '');
  setFields('facilityDetails', facilityDetails);
};

import { StopsMain } from './StopsMain';

interface StopEditorProps {
  stop: LastUsedTopStopModel | null;
  hasDropOffStop: boolean;
  dropOffOptions: MenuItemType[];
  pickUpOptions: MenuItemType[];
  stopIds: {
    id: number;
    locationName: string;
  }[];
  isSidebarRender?: boolean;
  mode: LoadModeTypes;
  currentNewStopId: number;
  currentNewLoadItemId: number;
  setActiveView: Setter<string>;
  activeView: Accessor<string>;
}

const StopEditor = (props: StopEditorProps) => {
  const [setAddressBook] = createSignal<CustomerAddress[]>([]);
  const [smallestLoadItemId, setSmallestLoadItemId] = createSignal<number>(
    props.currentNewLoadItemId,
  );

  const modalId = 'stops-modal';
  const validateStopForm = validateSchema(stopValidations());

  const {
    form,
    errors,
    data,
    setFields,
    isDirty,
    setIsDirty,
    handleSubmit: formSubmit,
  } = createForm<InitialStopFormValues>({
    initialValues: {
      ...initialFormValues,
      mode: orderStore.order.loads[orderStore.activeTab.index]?.mode,
      loadItems: [],
      shouldSendEdiBooleanVals: props.stop?.shouldSendEdiBooleanVals,
    },
    validate: async (values) =>
      fillBreakdownErrorHoles(await validateStopForm(values)),
    onSubmit: () => {
      if (requoteRequired()) {
        handleToast(
          ToastType.Error,
          'A new quote is required before saving this stop.',
        );
        return;
      }
      setOrderStore('isStopsEdited', isDirty());
      handleStopSubmit();
    },
  });

  createEffect(() => {
    if (props.stop) {
      setFieldsForEditStop(props.stop);
    }
    if (!props.stop && data().id !== props.currentNewStopId) {
      setFields('id', props.currentNewStopId);
    }
  });
  createEffect(() => {
    if (
      orderStore.order.loads[orderStore.activeTab.index].mode !== data().mode
    ) {
      setFields(
        'mode',
        orderStore.order.loads[orderStore.activeTab.index]
          .mode as LoadModeTypes,
      );
    }
  });

  // eslint-disable-next-line complexity
  const handleStopSubmit = () => {
    const stopsInStore = [
      ...(orderStore.order.loads[orderStore.activeTab.index].stops ?? []),
    ];
    const customerId = Boolean(orderStore.order.customerId)
      ? orderStore.order.customerId
      : 0;
    if (data().id === 0) {
      setFields('id', props.currentNewStopId);
    }
    const {
      loadItems,
      driverDateIn,
      driverDateOut,
      driverTimeIn,
      driverTimeOut,
      dateAppointment,
      timeAppointment,
      time,
      stopDateTime,
      appointmentSchedulingStatus,
      aiSchedulingEnabled,
      appointmentPreference,
      ...rest
    } = data();
    const usesFacilityAppointmentRange =
      props.mode === 'TL' &&
      isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)() &&
      rest.facilityAppointmentType === 'Appointment' &&
      isFeatureFlagEnabled(FeatureFlags.MilitaryTime)();
    const activeAppointmentDate = usesFacilityAppointmentRange
      ? stopDateTime
      : dateAppointment;
    const activeAppointmentTime = usesFacilityAppointmentRange
      ? timeIntegerToTimeString(rest.beginTimeRange)
      : timeAppointment;
    const savedAppointmentDate =
      activeAppointmentTime === '' && activeAppointmentDate !== ''
        ? convertDateTimeV2(activeAppointmentDate, activeAppointmentDate)
        : activeAppointmentTime && activeAppointmentDate
          ? convertDateTimeV2(activeAppointmentDate, activeAppointmentTime)
          : '';
    let stop: Partial<LastUsedTopStopModel> = {
      ...rest,
      stopDateTime,
      ...appointmentSchedulingStopFields(
        {
          appointmentSchedulingStatus,
          aiSchedulingEnabled,
          appointmentPreference,
          ...rest,
          dateAppointment: activeAppointmentDate,
          timeAppointment: activeAppointmentTime,
          stopDateTime,
        },
        props.mode === 'TL' &&
          isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)(),
      ),
      id: data().id,
      operationType: data().operationType as OperationTypeV4,
      customerId: customerId!,
      loadId: orderStore.order.loads[orderStore.activeTab.index].id,
      phone: data().phone.replace(/[^0-9]/g, ''),
      time: time,
      driverInTime:
        driverTimeIn === '' && driverDateIn !== ''
          ? convertDateTimeV2(driverDateIn, driverDateIn)
          : driverDateIn && driverTimeIn
            ? convertDateTimeV2(driverDateIn, driverTimeIn)
            : '',
      driverOutTime:
        driverTimeOut === '' && driverDateOut !== ''
          ? convertDateTimeV2(driverDateOut, driverDateOut)
          : driverDateOut && driverTimeOut
            ? convertDateTimeV2(driverDateOut, driverTimeOut)
            : '',
      appointmentDate: savedAppointmentDate,
    };
    if (!props.stop) {
      stop.createdDate = new Date().toISOString();
      stop.timeZoneOffset = new Date().getTimezoneOffset();
      stop.driverOut = {
        date: DateTime.fromISO(driverDateOut).toFormat('MM/dd/yyyy'),
        time: DateTime.fromISO(driverTimeOut).toFormat('hh:mm a'),
      };
      stop.driverIn = {
        date: DateTime.fromISO(driverDateIn).toFormat('MM/dd/yyyy'),
        time: DateTime.fromISO(driverTimeIn).toFormat('hh:mm a'),
      };
      if (stop.needsAppointment ?? false) {
        stop.appointmentDate = savedAppointmentDate;
        const displayTime = activeAppointmentTime.includes('T')
          ? DateTime.fromISO(activeAppointmentTime).toFormat('hh:mm a')
          : activeAppointmentTime;
        stop.appointment = {
          date: DateTime.fromISO(activeAppointmentDate).toFormat('MM/dd/yyyy'),
          time: displayTime,
        };
      }
      // These values are required for the UI to work
      stop.arrived = false;
      let highestStopOrder = -1;
      stopsInStore.forEach((stop) => {
        if ((stop.stopOrder ?? -1) > highestStopOrder) {
          highestStopOrder = stop.stopOrder ?? 0;
        }
      });
      stop.stopOrder = highestStopOrder + 1;
      stopsInStore.push(stop as LastUsedTopStopModel);
    } else {
      stop.modifiedDate = new Date().toISOString();
      if (stop.needsAppointment ?? false) {
        stop.appointmentDate = savedAppointmentDate;
      }
      const index = stopsInStore.findIndex((itm) => itm.id === props.stop?.id);
      stop = { ...props.stop, ...stop };
      if (
        props.stop.id > 0 &&
        props.mode !== 'LTL' &&
        stop.shouldSendEdiBooleanVals
      ) {
        const ediUpdates = createShowAuto214(
          stop as LastUsedTopStopModel & {
            driverInTimeZone: string;
            driverOutTimeZone: string;
            appointmentTimeZone: string;
          },
        );
        stop.ediUpdates = ediUpdates;
        //NOTE: Show the toast only if there are EDI updates
        ediUpdates.length > 0 &&
          handleToast(
            ToastType.Info,
            'Automatic EDI update queued. Save the load to send it.',
          );
      }
      stopsInStore[index] = stop as LastUsedTopStopModel;
    }
    const { stops } = orderStore.order.loads[orderStore.activeTab.index];

    const firstRelevantStop = find(stops, (s) => {
      return s.dropOff === stop.pickUp && s.operationType !== 'Delete';
    });
    const items = loadItems.map((item) => {
      let itemLoadHazmat = item.loadItemHazmatDetail;
      if (itemLoadHazmat) {
        itemLoadHazmat = {
          ...itemLoadHazmat,
          contactNumber:
            item.loadItemHazmatDetail !== null &&
            item.loadItemHazmatDetail !== undefined
              ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                Boolean(item.loadItemHazmatDetail.contactNumber)
                ? item.loadItemHazmatDetail.contactNumber.replace(/[^0-9]/g, '')
                : ''
              : '',
        };
      }
      const itm = {
        ...item,
        loadItemHazmatDetail: itemLoadHazmat,
      };

      if (Boolean(stop.dropOff) && stop.id === itm.dropOffId) {
        itm.pickUpId =
          itm.pickUpId !== null
            ? itm.pickUpId
            : firstRelevantStop
              ? firstRelevantStop.id
              : null;
      } else if (Boolean(stop.pickUp) && stop.id === itm.pickUpId) {
        itm.dropOffId =
          itm.dropOffId !== null
            ? itm.dropOffId
            : firstRelevantStop
              ? firstRelevantStop.id
              : null;
      }
      return itm;
    });
    //on Each stop change we update the dropTrailer on load.
    const hasDropTrailer = stopsInStore.some(
      (s) => s.operationType !== 'Delete' && s.dropTrailer === true,
    );
    updateLoadPropertyAtIndex({
      stops: stopsInStore,
      items: items,
      dropTrailer: hasDropTrailer,
    });

    if (ltlQuoteState.quote) {
      const newQuoteInfo = { ...ltlQuoteState.quote };
      stopsInStore.forEach((stop) => {
        if (Boolean(stop.origin) || stop.pickUp) {
          newQuoteInfo.originCity = stop.city;
          newQuoteInfo.originState = stop.state;
          newQuoteInfo.originZip = stop.zip;
          newQuoteInfo.originCountry = stop.countryCode;
          newQuoteInfo.originAddressMismatch = false;
        } else if (Boolean(stop.destination) || stop.dropOff) {
          newQuoteInfo.destinationCity = stop.city;
          newQuoteInfo.destinationState = stop.state;
          newQuoteInfo.destinationZip = stop.zip;
          newQuoteInfo.destinationCountry = stop.countryCode;
          newQuoteInfo.destinationAddressMismatch = false;
        }
      });
      const isMixedCommodityOnLoad = isFeatureFlagEnabled(
        FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
      )();
      const activeItems = items.filter(
        (item) => item.operationType !== 'Delete',
      );
      const previousQuoteItems = ltlQuoteState.quote.loadItems ?? [];
      const countsMatch = activeItems.length === previousQuoteItems.length;
      // How many load items point at each quote item; a legacy quote item
      // that fanned out into several load items cannot be attributed 1:1.
      const loadItemsPerQuoteItem = new Map<string, number>();
      activeItems.forEach((item) => {
        if (!item.ltlQuoteLoadItemId) return;
        const key = String(item.ltlQuoteLoadItemId);
        loadItemsPerQuoteItem.set(
          key,
          (loadItemsPerQuoteItem.get(key) ?? 0) + 1,
        );
      });

      // Legacy load items have no sub-item collection at all. Keep the quoted
      // sub-items for the quote item this load item came from, so saving a
      // stop cannot erase the quote's mixed commodities from ltlQuoteState.
      // An explicit empty collection is a user deletion and is mapped as-is.
      const quotedBreakdownFor = (
        item: (typeof activeItems)[number],
        index: number,
      ) => {
        const quoteItemId = item.ltlQuoteLoadItemId
          ? String(item.ltlQuoteLoadItemId)
          : undefined;
        const byId =
          quoteItemId !== undefined &&
          loadItemsPerQuoteItem.get(quoteItemId) === 1
            ? previousQuoteItems.find(
                (quoteItem) =>
                  String(quoteItem.ltlQuoteLoadItemId ?? '') === quoteItemId,
              )
            : undefined;
        const previous =
          byId ?? (countsMatch ? previousQuoteItems[index] : undefined);
        return previous?.quoteLoadItemBreakdown ?? [];
      };

      newQuoteInfo.loadItems = activeItems.map((item, index) => {
        return {
          ...item,
          weight: Number(item.weight),
          type: item.type ?? undefined,
          ...(isMixedCommodityOnLoad
            ? {
                quoteLoadItemBreakdown:
                  item.loadItemBreakdowns != null
                    ? mapLoadBreakdownsToQuoteBreakdowns(
                        item.loadItemBreakdowns,
                      )
                    : quotedBreakdownFor(item, index),
              }
            : {}),
        };
      });
      setLtlQuoteState('quote', newQuoteInfo);
    }
    closeModal(modalId);
  };

  const checkCustomerFeatureFlags = (feature: string) => {
    return get(
      featureFlagGetCustomerFeatures,
      `featureFlagCustomer.${feature}`,
      false,
    );
  };
  // eslint-disable-next-line complexity
  const createShowAuto214 = (
    stop: LastUsedTopStopModel & {
      driverInTimeZone: string;
      driverOutTimeZone: string;
      appointmentTimeZone: string;
    },
  ) => {
    let ediUpdates = stop.ediUpdates ? [...stop.ediUpdates] : [];
    if (stop.pickUp) {
      ediUpdates = ediUpdates.filter(
        (update) =>
          !['X1', 'CD', 'AB'].includes(update.shipmentStatus ?? '') &&
          update.appointmentStatus !== 'AB',
      );
    } else if (stop.dropOff) {
      ediUpdates = ediUpdates.filter(
        (update) =>
          !['X3', 'AF', 'AA'].includes(update.shipmentStatus ?? '') &&
          update.appointmentStatus !== 'AA',
      );
    }
    //remove the edi updates that are not relevant to the current stop
    const findIndexByKey = (key: keyof EdiUpdate, value: string) => {
      return ediUpdates.findIndex((item) => item[key] === value);
    };

    const addOrUpdateEdiUpdate = (
      key: keyof EdiUpdate,
      value: string,
      update: EdiUpdate,
    ) => {
      const index = findIndexByKey(key, value);
      if (index === -1) {
        ediUpdates.push(update);
      } else {
        ediUpdates[index] = { ...ediUpdates[index], ...update };
      }
    };

    const { driverInTimeZone, driverOutTimeZone, appointmentTimeZone } = stop;
    //Note: THese are required for any EDI Update
    const defaultEdiProperties = {
      latitude: null,
      longitude: null,
      note: null,
      auto214: true,
      statusReason: 'NS',
      temperature: null,
      stopId: stop.id,
      loadId: stop.loadId,
      state: stop.state ?? '',
      city: stop.city ?? '',
    };
    if (stop.pickUp) {
      const {
        shouldSendPickupAppointmentAuto214,
        shouldSendPickupDriverInAuto214,
        shouldSendPickupDriverOutAuto214,
      } = stop.shouldSendEdiBooleanVals;

      if (
        shouldSendPickupDriverInAuto214 &&
        checkCustomerFeatureFlags('EDIPickupDriverInAuto214')
      ) {
        addOrUpdateEdiUpdate('shipmentStatus', 'X3', {
          shipmentStatus: 'X3',
          appointmentStatus: null,
          date: stop.driverInTime as string,
          appointmentNumber: null,
          timeZone: driverInTimeZone || 'LT',
          ...defaultEdiProperties,
        });
      }
      if (
        shouldSendPickupDriverOutAuto214 &&
        checkCustomerFeatureFlags('EDIPickupDriverOutAuto214')
      ) {
        addOrUpdateEdiUpdate('shipmentStatus', 'AF', {
          shipmentStatus: 'AF',
          appointmentStatus: null,
          date: stop.driverOutTime as string,
          appointmentNumber: null,
          timeZone: driverOutTimeZone || 'LT',
          ...defaultEdiProperties,
        });
      }
      if (
        shouldSendPickupAppointmentAuto214 &&
        checkCustomerFeatureFlags('EDIPickupAppointmentAuto214')
      ) {
        addOrUpdateEdiUpdate('appointmentStatus', 'AA', {
          shipmentStatus: null,
          appointmentStatus: 'AA',
          date: stop.appointmentDate as string,
          appointmentNumber: stop.appointmentNum as string,
          timeZone: appointmentTimeZone || 'LT',
          ...defaultEdiProperties,
        });
      }
    }

    if (stop.dropOff) {
      const {
        shouldSendDropoffAppointmentAuto214,
        shouldSendDropoffDriverInAuto214,
        shouldSendDropoffDriverOutAuto214,
      } = stop.shouldSendEdiBooleanVals;
      if (
        shouldSendDropoffDriverInAuto214 &&
        checkCustomerFeatureFlags('EDIDropoffDriverInAuto214')
      ) {
        addOrUpdateEdiUpdate('shipmentStatus', 'X1', {
          shipmentStatus: 'X1',
          appointmentStatus: null,
          date: stop.driverInTime as string,
          appointmentNumber: null,
          timeZone: driverInTimeZone || 'LT',
          ...defaultEdiProperties,
        });
      }
      if (
        shouldSendDropoffDriverOutAuto214 &&
        checkCustomerFeatureFlags('EDIDropoffDriverOutAuto214')
      ) {
        addOrUpdateEdiUpdate('shipmentStatus', 'CD', {
          shipmentStatus: 'CD',
          appointmentStatus: null,
          date: stop.driverOutTime as string,
          appointmentNumber: null,
          timeZone: driverOutTimeZone || 'LT',
          ...defaultEdiProperties,
        });
      }
      if (
        shouldSendDropoffAppointmentAuto214 &&
        checkCustomerFeatureFlags('EDIDropoffAppointmentAuto214')
      ) {
        addOrUpdateEdiUpdate('appointmentStatus', 'AB', {
          shipmentStatus: null,
          appointmentStatus: 'AB',
          date: stop.appointmentDate as string,
          appointmentNumber: stop.appointmentNum as string,
          timeZone: appointmentTimeZone || 'LT',
          ...defaultEdiProperties,
        });
      }
    }
    // Remove EDI updates if the corresponding shouldSend flags are false
    ediUpdates = ediUpdates.filter((update) => {
      if (
        update.shipmentStatus === 'X3' &&
        !stop.shouldSendEdiBooleanVals.shouldSendPickupDriverInAuto214
      ) {
        return false;
      }
      if (
        update.shipmentStatus === 'AF' &&
        !stop.shouldSendEdiBooleanVals.shouldSendPickupDriverOutAuto214
      ) {
        return false;
      }
      if (
        update.appointmentStatus === 'AA' &&
        !stop.shouldSendEdiBooleanVals.shouldSendPickupAppointmentAuto214
      ) {
        return false;
      }
      if (
        update.shipmentStatus === 'X1' &&
        !stop.shouldSendEdiBooleanVals.shouldSendDropoffDriverInAuto214
      ) {
        return false;
      }
      if (
        update.shipmentStatus === 'CD' &&
        !stop.shouldSendEdiBooleanVals.shouldSendDropoffDriverOutAuto214
      ) {
        return false;
      }
      if (
        update.appointmentStatus === 'AB' &&
        !stop.shouldSendEdiBooleanVals.shouldSendDropoffAppointmentAuto214
      ) {
        return false;
      }
      return true;
    });
    return ediUpdates;
  };

  // eslint-disable-next-line complexity
  const setFieldsForEditStop = (stop: LastUsedTopStopModel) => {
    setFields('address1', stop.address1 ?? '');
    setFields('address2', stop.address2 ?? '');
    setFields('city', stop.city ?? '');
    setFields('state', stop.state ?? '');
    setFields('zip', stop.zip);
    setFields('contact', stop.contact ?? '');
    setFields('timezone', stop.timezone ?? '');
    setFields('beginTimeRange', stop.beginTimeRange ?? null);
    setFields('endTimeRange', stop.endTimeRange ?? null);
    setFields('timeComments', stop.timeComments ?? '');
    setFields('locationName', stop.locationName ?? '');
    setFields('phone', stop.phone ?? '');
    setFields('email', stop.email ?? '');
    setFields('description', stop.description ?? '');
    setFields('instructions', stop.instructions ?? '');
    setFields('directions', stop.directions ?? '');
    setFields('stopDateTime', stop.stopDateTime ?? '');
    setFields('dateAppointment', stop.appointmentDate ?? '');
    setFields('timeAppointment', stop.appointmentDate ?? '');
    setFields('referenceNum', stop.referenceNum ?? '');
    setFields('time', stop.time ?? '');
    setFields('pickUp', stop.pickUp);
    setFields('dropOff', stop.dropOff);
    setFields('countryCode', stop.countryCode ?? 'USA');
    setFields('driverDateIn', stop.driverInTime ?? '');
    setFields('driverTimeIn', stop.driverInTime ?? '');
    setFields('driverDateOut', stop.driverOutTime ?? '');
    setFields('driverTimeOut', stop.driverOutTime ?? '');
    setFields('needsAppointment', stop.needsAppointment);
    setFields(
      'appointmentSchedulingStatus',
      stop.appointmentSchedulingStatus ?? '',
    );
    setFields('aiSchedulingEnabled', stop.aiSchedulingEnabled ?? false);
    setFields('appointmentPreference', stop.appointmentPreference ?? '');
    setFields('dropTrailer', stop.dropTrailer ?? false);
    setFields(
      'operationType',
      stop.operationType === 'None' ? 'Update' : stop.operationType,
    );
    setFields('id', stop.id);
    setFields(
      'loadItems',
      orderStore.order.loads[orderStore.activeTab.index].items ?? [],
    );
    setFields('appointmentNum', stop.appointmentNum ?? '');
    setFields(
      'facilityDetails',
      stop.facilityDetails
        ? normalizeFacilityDetails(stop.facilityDetails)
        : createStopFacilityDetails(),
    );
    setFields(
      'facilityAppointmentType',
      stop.facilityDetails?.appointmentType ??
        stop.facilityAppointmentType ??
        '',
    );
    setFields(
      'facilitySchedulingMethod',
      stop.facilityDetails?.schedulingMethod ??
        stop.facilitySchedulingMethod ??
        '',
    );
    if (orderStore.order.edi) {
      setFields('stopCode', stop.stopCode ?? '');
      setFields('proprietaryCode', stop.proprietaryCode ?? '');
    }
    // Setting for Edi updates
    setFields('shouldSendEdiBooleanVals', stop.shouldSendEdiBooleanVals);
  };

  const addListItemFromLTLCatalog = (item: LTLCatalogs) => {
    const currentLoadItems = data().loadItems;
    const newId = smallestLoadItemId();
    const catalogLineItem: PickUpDropOffItem = {
      item: item.unitType || 'Pallets',
      class: item.class?.toString() ?? null,
      nmfc: item.nmfc,
      description: item.description,
      lengthInch: item.lengthInches,
      widthInch: item.widthInches,
      heightInch: item.heightInches,
      id: newId,
      operationType: 'Insert',
      loadId: orderStore.order.loads[orderStore.activeTab.index].id,
      dropOffId: data().dropOff ? data().id : null,
      pickUpId: data().pickUp ? data().id : null,
      quantity: 1,
      weight: item.weight != null ? Number(item.weight) : 0,
      pieces: item.pieces != null ? String(item.pieces) : '1',
      hazmat: Boolean(item.hazmat),
      loadItemHazmatDetail: item.hazmat
        ? {
            hazardClass: item.hazardClass || '',
            packingGroup: item.packingGroup || '',
            unNumber: item.unNumber || '',
            contactName: item.contactName || '',
            contactNumber: item.contactNumber || '',
            piece: item.piece || '',
            operationType: 'Insert',
            loadItemId: newId,
          }
        : null,
      createdBy: null,
      charge: null,
      uom: 'lbs',
      volume: null,
      height: null,
      length: 0,
      width: null,
      descriptionWithParanthesis: '',
      displayDimensions: '',
      createdDate: null,
      historyContext: null,
      modifiedDate: null,
      modifiedBy: null,
      load: null,
      dropOff: null,
      pickUp: null,
      suggestedClass: null,
      hazmatItems: null,
      hazmatDescription: null,
      type: null,
      pcf: '',
    };

    const isLineItemReplaceable = (lineItem: PickUpDropOffItem) => {
      const normalizeToNumber = (value: unknown): number | null => {
        if (value === null || value === undefined || value === '') {
          return null;
        }
        if (typeof value === 'number') {
          return Number.isNaN(value) ? null : value;
        }
        const num = Number(value);
        return Number.isNaN(num) ? null : num;
      };

      const quantityValue = normalizeToNumber(lineItem.quantity as unknown);
      const isDefaultQuantity = quantityValue === 1 || quantityValue === null;
      const isDefaultItem = lineItem.item === 'Pallets';
      const piecesValue = normalizeToNumber(lineItem.pieces as unknown);
      const isDefaultPieces = piecesValue === 1 || piecesValue === null;
      const {
        description,
        weight,
        lengthInch,
        widthInch,
        heightInch,
        nmfc,
        class: classValue,
        loadItemHazmatDetail,
        hazmat,
      } = lineItem;
      const emptyChecks = {
        description: !description,
        weight: !weight,
        lengthInch: !lengthInch,
        widthInch: !widthInch,
        heightInch: !heightInch,
        nmfc: !nmfc,
        classValue: !classValue,
        loadItemHazmatDetail: !loadItemHazmatDetail,
        hazmat: !hazmat,
      };
      const isEmpty = Object.values(emptyChecks).every(Boolean);
      return isDefaultQuantity && isDefaultItem && isDefaultPieces && isEmpty;
    };

    const replaceableLineItemIndex = currentLoadItems.findIndex(
      isLineItemReplaceable,
    );

    if (replaceableLineItemIndex !== -1) {
      const updatedItems = currentLoadItems.map((item, index) =>
        index === replaceableLineItemIndex ? catalogLineItem : item,
      );
      setFields('loadItems', updatedItems);
    } else {
      setFields('loadItems', [...currentLoadItems, catalogLineItem]);
    }
    setSmallestLoadItemId(smallestLoadItemId() - 1);
    props.setActiveView('stops-main');
  };

  const normalizeAddressBookTimeForStop = (
    openTime: string | Date | null | undefined,
    stopDate: string,
  ) => {
    if (openTime === undefined || openTime === null || openTime === '') {
      return '';
    }

    const normalizedStopDate = stopDate.split('T')[0] || '';
    const openTimeValue =
      openTime instanceof Date
        ? DateTime.fromJSDate(openTime).toFormat('HH:mm:ss')
        : openTime.toString();

    if (!normalizedStopDate) {
      return openTimeValue;
    }

    if (openTimeValue.includes('T')) {
      const parsedDateTime = DateTime.fromISO(openTimeValue, { setZone: true });
      if (parsedDateTime.isValid) {
        return `${normalizedStopDate}T${parsedDateTime.toFormat('HH:mm:ss')}`;
      }
    }

    const parsedTime = DateTime.fromFormat(openTimeValue, 'HH:mm:ss').isValid
      ? DateTime.fromFormat(openTimeValue, 'HH:mm:ss')
      : DateTime.fromFormat(openTimeValue, 'HH:mm');

    if (parsedTime.isValid) {
      return `${normalizedStopDate}T${parsedTime.toFormat('HH:mm:ss')}`;
    }

    return openTimeValue;
  };

  const handleAddressClick = (rowData: CustomerAddress) => {
    setFields('locationName', rowData.name ?? '');
    setFields('address1', rowData.addressLine1 ?? '');
    setFields('address2', rowData.addressLine2 ?? '');
    setFields('city', rowData.city ?? '');
    setFields('state', rowData.state ?? '');
    setFields('zip', rowData.zip ?? '');
    setFields('contact', rowData.contactName ?? '');
    setFields('phone', rowData.contactPhone ?? '');
    setFields('email', rowData.contactEmail ?? '');
    setFields('instructions', rowData.specialInstructions ?? '');
    setFields('description', rowData.internalNote ?? '');
    setFields('directions', rowData.drivingDirections ?? '');
    copyFacilityDetailsToStop(rowData, setFields, props.mode);
    setIsDirty(true);
    if (props.mode === 'LTL') {
      const openTimeFrom = normalizeAddressBookTimeForStop(
        rowData.openTimeFrom,
        data().driverDateIn || data().stopDateTime,
      );
      setFields('driverTimeIn', openTimeFrom);
      const openTimeTo = normalizeAddressBookTimeForStop(
        rowData.openTimeTo,
        data().driverDateOut || data().stopDateTime,
      );
      setFields('driverTimeOut', openTimeTo);
    }
    props.setActiveView('stops-main');
  };

  const requoteRequired = createMemo(() => {
    if (!props.stop) return false;
    const mode = orderStore.order.loads[orderStore.activeTab.index]?.mode;
    if (mode !== 'LTL') return false;
    if (props.stop.pickUp) {
      return ltlQuoteState.quote?.originAddressMismatch === true;
    }
    if (props.stop.dropOff) {
      return ltlQuoteState.quote?.destinationAddressMismatch === true;
    }
    return false;
  });

  const stopCanSave = async () => {
    if (requoteRequired()) {
      handleToast(
        ToastType.Error,
        'A new quote is required before saving this stop.',
      );
      return false;
    }
    try {
      await stopValidations().validate(data(), { abortEarly: false });
    } catch {
      void formSubmit();
      return false;
    }
    return true;
  };

  const createInitialStopValsForEdi = () => {
    const { quoteStops = [] } =
      orderStore.order.loads[orderStore.activeTab.index];
    const stop = quoteStops.find((stop) => stop.id === props.stop?.id);
    return {
      driverDateIn: stop?.driverInTime ?? '',
      driverDateOut: stop?.driverOutTime ?? '',
      appointmentNum: stop?.appointmentNum ?? '',
      appointmentDate: stop?.appointmentDate ?? '',
    };
  };
  return (
    <>
      <div class={props.activeView() === 'stops-main' ? 'block p-2' : 'hidden'}>
        <StopsMain
          hasDropOffStop={props.hasDropOffStop}
          form={form}
          setActiveView={props.setActiveView}
          setAddressBook={setAddressBook}
          errors={
            errors as unknown as Accessor<
              Record<keyof InitialStopFormValues, string[] | null>
            >
          }
          setIsDirty={setIsDirty}
          data={data}
          setFields={setFields}
          setSmallestLoadItemId={setSmallestLoadItemId}
          smallestLoadItemId={smallestLoadItemId}
          modalId={modalId}
          dropOffOptions={props.dropOffOptions}
          pickUpOptions={props.pickUpOptions}
          mode={props.mode}
          stop={props.stop}
          initialStopVals={createInitialStopValsForEdi()}
          stopIds={props.stopIds}
          handleSubmit={formSubmit}
          requoteRequired={requoteRequired()}
          stopCanSave={stopCanSave}
        />
      </div>
      <div
        class={
          props.activeView() === 'ltl-product-catalog' ? 'block' : 'hidden'
        }
      >
        <LTLProductCatalog
          customerId={orderStore.order.customerId!}
          goBackButtonText="< Back to Stops"
          goBack={() => props.setActiveView('stops-main')}
          onRowClick={(rowData: LTLCatalogs) => {
            addListItemFromLTLCatalog(rowData);
          }}
          closeModal={closeModal}
          openModal={openModal}
        />
      </div>
      <div
        class={
          props.activeView() === 'select-from-addresses' ? 'block' : 'hidden'
        }
      >
        <Show
          when={orderStore.order.customerId != null}
          fallback={<Box p={2}>No customer selected.</Box>}
        >
          <Box class="ag-theme-alpine" height={550}>
            <AddressBookGrid
              onRowClick={(rowData: CustomerAddress) => {
                handleAddressClick(rowData);
              }}
              customerId={orderStore.order.customerId!}
              type="Stops"
            />
          </Box>
        </Show>
      </div>
    </>
  );
};

export default StopEditor;
